import { defineStore } from 'pinia'
import api from '@/api/axios'
import { ref, computed } from 'vue'

export const useDataReviewStore = defineStore('dataReview', () => {
  // Core state
  const loading = ref(false)
  const error = ref(null)
  const reviewData = ref(null)

  // Data review operations
  async function getReviewSummary(projectId, sessionId) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/research/projects/${projectId}/sessions/${sessionId}/review/summary`)
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to get review summary'
      console.error('Review summary error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  async function updateReviewStatus(projectId, sessionId, itemId, status, notes = '') {
    if (!projectId || !sessionId || !itemId) throw new Error('Project ID, Session ID, and Item ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      const response = await api.put(`/research/projects/${projectId}/sessions/${sessionId}/review/items/${itemId}`, {
        status,
        notes
      })
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update review status'
      console.error('Review status update error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  async function bulkUpdateReviewStatus(projectId, sessionId, updates) {
    if (!projectId || !sessionId || !updates?.length) throw new Error('Project ID, Session ID, and updates are required')
    
    loading.value = true
    error.value = null
    
    try {
      const response = await api.put(`/research/projects/${projectId}/sessions/${sessionId}/review/bulk`, {
        updates
      })
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to bulk update review status'
      console.error('Bulk review update error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  async function getDataCollectionReview(projectId, sessionId, filters = {}) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      const params = new URLSearchParams()
      
      // Apply filters
      if (filters.page) params.append('page', filters.page)
      if (filters.limit) params.append('limit', filters.limit)
      if (filters.sortBy) params.append('sortBy', filters.sortBy)
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder)
      if (filters.categoryId) params.append('category', filters.categoryId)
      if (filters.status && filters.status !== 'all') params.append('status', filters.status)

      const endpoint = `/research/projects/${projectId}/sessions/${sessionId}/data-collection/review?${params.toString()}`
      console.log('🔍 DataReview Store: Making request to:', endpoint)

      const response = await api.get(endpoint)
      
      // Store the processed data in reviewData
      reviewData.value = response.data
      console.log('🔍 DataReview Store: Stored reviewData:', reviewData.value)
      
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch data collection review'
      console.error('Data collection review error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  async function getReviewItems(projectId, sessionId, options = {}) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      const params = new URLSearchParams()
      if (options.status) params.append('status', options.status)
      if (options.category) params.append('category', options.category)
      if (options.page) params.append('page', options.page)
      if (options.limit) params.append('limit', options.limit)
      
      const queryString = params.toString()
      const endpoint = `/research/projects/${projectId}/sessions/${sessionId}/review/items${queryString ? `?${queryString}` : ''}`
      
      const response = await api.get(endpoint)
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to get review items'
      console.error('Review items error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  async function exportReviewData(projectId, sessionId, format = 'json') {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/research/projects/${projectId}/sessions/${sessionId}/review/export`, {
        params: { format },
        responseType: format === 'csv' ? 'blob' : 'json'
      })
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to export review data'
      console.error('Review export error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  async function getReviewStatistics(projectId, sessionId) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/research/projects/${projectId}/sessions/${sessionId}/review/statistics`)
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to get review statistics'
      console.error('Review statistics error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  // Article management methods
  async function rejectArticle(projectId, sessionId, articleId, reason = '') {
    if (!projectId || !sessionId || !articleId) throw new Error('Project ID, Session ID, and Article ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post(
        `/research/projects/${projectId}/sessions/${sessionId}/data-collection/articles/reject`,
        { 
          articleIds: [articleId],
          rejectionReason: reason 
        }
      )
      
      // Update local state if reviewData exists
      if (reviewData.value?.articles?.data) {
        const article = reviewData.value.articles.data.find(a => a.id === articleId)
        if (article) {
          article.rejected = true
          article.rejectedAt = new Date().toISOString()
          article.rejectionReason = reason
        }
      }
      
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to reject article'
      console.error('Article rejection error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  async function approveArticle(projectId, sessionId, articleId) {
    if (!projectId || !sessionId || !articleId) throw new Error('Project ID, Session ID, and Article ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post(
        `/research/projects/${projectId}/sessions/${sessionId}/data-collection/articles/restore`,
        { articleIds: [articleId] }
      )
      
      // Update local state if reviewData exists
      if (reviewData.value?.articles?.data) {
        const article = reviewData.value.articles.data.find(a => a.id === articleId)
        if (article) {
          article.rejected = false
          article.rejectedAt = null
          article.rejectionReason = null
        }
      }
      
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to approve article'
      console.error('Article approval error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  async function bulkRejectArticles(projectId, sessionId, articleIds, reason = '') {
    if (!projectId || !sessionId || !articleIds?.length) throw new Error('Project ID, Session ID, and Article IDs are required')
    
    loading.value = true
    error.value = null
    console.log('Bulk rejecting articles:', articleIds, 'Reason:', reason)
    try {
      const response = await api.post(
        `/research/projects/${projectId}/sessions/${sessionId}/data-collection/articles/reject`,
        { 
          articleIds,
          rejectionReason: reason 
        }
      )
      
      // Update local state if reviewData exists
      if (reviewData.value?.articles?.data) {
        reviewData.value.articles.data.forEach(article => {
          if (articleIds.includes(article.id)) {
            article.rejected = true
            article.rejectedAt = new Date().toISOString()
            article.rejectionReason = reason
          }
        })
      }
      
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to bulk reject articles'
      console.error('Bulk rejection error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  async function bulkApproveArticles(projectId, sessionId, articleIds) {
    if (!projectId || !sessionId || !articleIds?.length) throw new Error('Project ID, Session ID, and Article IDs are required')
    
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post(
        `/research/projects/${projectId}/sessions/${sessionId}/data-collection/articles/approve`,
        { articleIds }
      )
      
      // Update local state if reviewData exists
      if (reviewData.value?.articles?.data) {
        reviewData.value.articles.data.forEach(article => {
          if (articleIds.includes(article.id)) {
            article.rejected = false
            article.rejectedAt = null
            article.rejectionReason = null
          }
        })
      }
      
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to bulk approve articles'
      console.error('Bulk approval error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  // Computed properties
  const reviewArticles = computed(() => reviewData.value?.articles?.data || [])
  const reviewPagination = computed(() => reviewData.value?.articles?.pagination || null)
  const reviewOverview = computed(() => reviewData.value?.overview || null)
  const qualityMetrics = computed(() => reviewData.value?.overview?.qualityMetrics || null)
  const sourceAnalysis = computed(() => reviewData.value?.overview?.sourceAnalysis || [])
  const categoryBreakdown = computed(() => {
    const apiCategories = reviewData.value?.filters?.availableCategories || []
    if (apiCategories.length > 0) {
      const breakdown = {}
      apiCategories.forEach(category => {
        breakdown[category.id] = {
          count: category.totalCount,
          name: category.name,
          articleCount: category.articleCount,
          entityRecordCount: category.entityRecordCount,
          hasArticles: category.hasArticles,
          hasEntityRecords: category.hasEntityRecords
        }
      })
      return breakdown
    }
    
    // Fallback: Calculate from articles
    const articles = reviewData.value?.articles?.data || []
    const breakdown = {}
    articles.forEach(article => {
      const categoryId = article.categoryId
      if (categoryId) {
        if (!breakdown[categoryId]) {
          breakdown[categoryId] = {
            count: 0,
            name: categoryId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            articleCount: 0,
            entityRecordCount: 0,
            hasArticles: true,
            hasEntityRecords: false
          }
        }
        breakdown[categoryId].count++
        breakdown[categoryId].articleCount++
      }
    })
    return breakdown
  })
  const reviewOptions = computed(() => reviewData.value?.reviewOptions || {})
  const collectionMetadata = computed(() => reviewData.value?.collectionMetadata || {})
  const reviewEntityRecords = computed(() => reviewData.value?.entityRecords?.data || [])
  const entityRecordsCount = computed(() => reviewData.value?.entityRecords?.totalCount || 0)
  const reviewFallback = computed(() => reviewData.value?.fallback || null)

  return {
    // State
    loading,
    error,
    reviewData,
    
    // Computed properties
    reviewArticles,
    reviewPagination,
    reviewOverview,
    qualityMetrics,
    sourceAnalysis,
    categoryBreakdown,
    reviewOptions,
    collectionMetadata,
    reviewEntityRecords,
    entityRecordsCount,
    reviewFallback,
    
    // Review operations
    getDataCollectionReview,
    getReviewSummary,
    updateReviewStatus,
    bulkUpdateReviewStatus,
    getReviewItems,
    exportReviewData,
    getReviewStatistics,
    rejectArticle,
    approveArticle,
    bulkRejectArticles,
    bulkApproveArticles
  }
})